// MA-687 (RED round 1 MUST-1) — serve every distinct picture before any repeat.
//
// THE FINDING: ms.math.05's 80 foundationItems records (served by PACK_MERGE)
// draw only 13 distinct number bonds: 72 of them share picture AND answer with
// another record and differ only in prompt wording or one distractor. In a
// random 10-item draw the child saw the same bond and tapped the same answer
// twice in ~75% of sessions (the mathExemplars-only pool on master: 0%).
//
// THE RULE: in a merged (PACK_MERGE) pool, order items by OCCURRENCE RANK of
// their served-picture signature — every signature's first item, then every
// signature's second item, and so on — keeping the incoming order (the seeded
// shuffle, or raw pack order under the dev bypass) within each rank. The pool
// is unchanged as a set; only the order moves. Rank is global (difficulty is
// not part of the signature), so the guarantee is over the pool: every
// distinct picture in the pool comes before any repeat. Session selection
// (pickItemForTier) still picks by difficulty and breaks ties by pool order,
// so a skill whose merged pool mixes difficulties (ms.math.05: the d3 FI
// bonds beside d1 ME bonds) shifts some session share between them (RED
// round 2 measured about 7 points away from the 05 FI items).
//
// MA-818 (2026-10-06) — every LATER rank keeps the FIRST rank's task order
// (ties broken by the task's first position in the incoming pool, then by the
// item's own position). Before, rank 2 followed the second copies' own
// positions, so when a session truncates the pool mid-rank (ms.math.00d: 11
// tasks once 0 is served, 20 slots), a task early in rank 1 could be the one
// left without a second copy. The live run server takes the pool-order head
// of a difficulty band first, so a band holding {4, 3, 3} opened on the lone
// 4 and then had to serve 3, 3 back to back (72 of 2000 ms.math.00d L=20
// sessions, against MA-808's 0/2000). With rank order = first-rank order the
// tasks that miss a copy are the LAST of rank 1, so the head of a band is
// never a singleton beside a pair. Still order-only: the set, rank 1 and
// "every distinct picture before any repeat" are unchanged.
// RED round 1 N4: on ms.math.05 the raw pool order now has more adjacent
// same-signature pairs (59 -> 98 over 300 seeded pools); its sessions stay at
// 0 back-to-back repeats only because MA-808's tie-break in the pick absorbs
// them, so 05's session guarantee rests on that tie-break, not on pool order.
//
// The signature is what the child SEES and ANSWERS: the drawn picture
// (mathVisual, else the inline scaffold's data) and the keyed answer. Prompt
// wording and the option set are deliberately NOT part of it — a reworded
// prompt over the same bond with the same answer is the same task — and a
// concrete bond's side (known part left or right) is folded too: a mirrored
// bond is the same task.
//
// Pure — no React, no I/O.

export interface SignatureItem {
  answer?: unknown;
  prompt?: unknown;
  mathVisual?: unknown;
  visualScaffold?: { kind?: string; data?: unknown } | null;
}

function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(",")}]`;
  if (v && typeof v === "object") {
    return `{${Object.keys(v as Record<string, unknown>)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stable((v as Record<string, unknown>)[k])}`)
      .join(",")}}`;
  }
  return JSON.stringify(v ?? null);
}

/** A number bond drawn with its known part on the other side is the same
 *  task (same whole, same known part, same answer) — a mirrored near-
 *  duplicate, not a new picture (MA-687 RED round 1). */
function normalisedVisual(mv: unknown): unknown {
  const v = drawnVisual(mv) as { kind?: string; arrangement?: string } | null;
  if (v && v.kind === "number_bond" && v.arrangement === "concrete_known_right") {
    return { ...v, arrangement: "concrete" };
  }
  return v;
}

/** MA-824 — the drawn picture only: `displayTimeMs` (the timed quick look on
 *  ms.math.01 subitize cards) says how long the picture stays up, not what is
 *  drawn, so a timed and an untimed card with the same dots are the same
 *  picture for the repeat keys. */
function drawnVisual(mv: unknown): unknown {
  if (mv && typeof mv === "object" && "displayTimeMs" in (mv as Record<string, unknown>)) {
    const { displayTimeMs: _look, ...rest } = mv as Record<string, unknown>;
    return rest;
  }
  return mv;
}

/** The served-picture signature: picture + keyed answer. */
export function servedPictureSignature(item: SignatureItem): string {
  // RED round 2 NIT-6: an item with no picture at all is identified by its
  // prompt, so two text-only items with the same answer are not "the same
  // picture".
  const picture = item.mathVisual
    ? { mv: normalisedVisual(item.mathVisual) }
    : item.visualScaffold
      ? { scaffold: { kind: item.visualScaffold.kind, data: item.visualScaffold.data } }
      : { text: String(item.prompt ?? "") };
  return stable([String(item.answer ?? ""), picture]);
}

/** MA-808 — the served FORM: what the child reads, sees and answers (keyed
 *  answer + prompt + drawn picture). Two items with equal form keys are the
 *  same question on screen; only the option order can differ. The picture is
 *  the authored one, un-normalised (a mirrored bond is a different form but
 *  the same task — see repeatLevel). */
export function servedFormKey(item: SignatureItem): string {
  const picture = item.mathVisual
    ? { mv: drawnVisual(item.mathVisual) }
    : item.visualScaffold
      ? { scaffold: { kind: item.visualScaffold.kind, data: item.visualScaffold.data } }
      : null;
  return stable([String(item.answer ?? ""), String(item.prompt ?? ""), picture]);
}

const keyCache = new WeakMap<object, { sig: string; form: string }>();
function keysOf(item: SignatureItem): { sig: string; form: string } {
  const hit = keyCache.get(item as object);
  if (hit) return hit;
  const keys = { sig: servedPictureSignature(item), form: servedFormKey(item) };
  keyCache.set(item as object, keys);
  return keys;
}

/** MA-808 — the memoised task key (servedPictureSignature) of `item`. */
export function servedTaskKey(item: SignatureItem): string {
  return keysOf(item).sig;
}

/** MA-808 — how much `item` would repeat `previous` if served right after it:
 *  2 = the identical form, 1 = the same task (servedPictureSignature: same
 *  picture and answer) in a different form (a reworded prompt, a mirrored
 *  bond), 0 = a different task. A missing `previous` repeats nothing. */
export function repeatLevel(item: SignatureItem, previous: SignatureItem | null | undefined): 0 | 1 | 2 {
  if (!previous) return 0;
  const a = keysOf(item);
  const b = keysOf(previous);
  if (a.form === b.form) return 2;
  if (a.sig === b.sig) return 1;
  return 0;
}

/** Stable occurrence-rank interleave (see the header). Never adds, drops or
 *  duplicates an item. */
export function spreadRepeatedPictures<T extends SignatureItem>(pool: readonly T[]): T[] {
  const seen = new Map<string, number>();
  // MA-818 — each task's first position: later ranks follow rank 1's order.
  const firstAt = new Map<string, number>();
  const ranked = pool.map((item, index) => {
    const sig = servedPictureSignature(item);
    const rank = seen.get(sig) ?? 0;
    seen.set(sig, rank + 1);
    if (!firstAt.has(sig)) firstAt.set(sig, index);
    return { item, index, rank, first: firstAt.get(sig) as number };
  });
  ranked.sort((x, y) => x.rank - y.rank || x.first - y.first || x.index - y.index);
  return ranked.map((r) => r.item);
}

/** MA-1206 — MA-808's skip-a-repeat as an ORDER, for the served paths that
 *  take pool order with no picker (Foundation's items[done], the gated-off
 *  run draw). Two DISTINCT items can be the same question on screen
 *  (ms.math.11's ME0043 and ME0067 are both "9 + 2 = ?"), and those paths
 *  served them back to back. The rule is the live static draw's
 *  (selectNextItem drawStaticAvoiding): at each position take the head of
 *  what is left unless it repeats the item before it (repeatLevel > 0), then
 *  the first later item that repeats it least. Never adds, drops or
 *  duplicates an item; a pool with no back-to-back repeat keeps its exact
 *  order (byte-identical). A repeat is left only where everything left
 *  repeats (unavoidable). */
export function nextLeastRepeatIndex<T extends SignatureItem>(left: readonly T[], previous: T | null | undefined): number {
  if (left.length === 0) return -1;
  let idx = 0;
  let level = repeatLevel(left[0], previous);
  for (let i = 1; i < left.length && level > 0; i++) {
    const l = repeatLevel(left[i], previous);
    if (l < level) {
      idx = i;
      level = l;
    }
  }
  return idx;
}

export function spaceRepeatedForms<T extends SignatureItem>(pool: readonly T[]): T[] {
  const left = [...pool];
  const out: T[] = [];
  while (left.length > 0) {
    out.push(left.splice(nextLeastRepeatIndex(left, out[out.length - 1]), 1)[0]);
  }
  return out;
}
